import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Upload, FileText, Trash2, Plus, Sparkles, Download, AlertTriangle, RotateCw, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["timing", "dietary", "celebration", "vip", "activity", "special_request", "logistics", "payment", "other"];
const CAT_CLR = {
  timing: "bg-sky-50 text-sky-700 border-sky-200",
  dietary: "bg-red-50 text-red-700 border-red-200",
  celebration: "bg-pink-50 text-pink-700 border-pink-200",
  vip: "bg-violet-50 text-violet-700 border-violet-200",
  activity: "bg-emerald-50 text-emerald-700 border-emerald-200",
  special_request: "bg-amber-50 text-amber-700 border-amber-200",
  logistics: "bg-indigo-50 text-indigo-700 border-indigo-200",
  payment: "bg-orange-50 text-orange-700 border-orange-200",
  other: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export const BookingItineraryDialog = ({ open, onOpenChange, booking, onChanged }) => {
  const { hasPermission } = useAuth();
  const canUpload = hasPermission("bookings_itinerary");
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [it, setIt] = useState(null);
  const [pointers, setPointers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const applyItinerary = (itin) => {
    setIt(itin);
    setPointers((itin?.key_pointers || []).map((p) => ({ ...p })));
  };

  const fetchItinerary = async () => {
    try {
      const { data } = await api.get(`/bookings/${booking.id}/itinerary`);
      applyItinerary(data && data.status !== "none" ? data : null);
      if (data?.status === "processing") startPoll();
      else stopPoll();
    } catch { /* ignore */ }
  };

  const startPoll = () => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/bookings/${booking.id}/itinerary`);
        if (data?.status !== "processing") { applyItinerary(data); stopPoll(); onChanged?.(); }
      } catch { /* ignore */ }
    }, 3000);
  };

  useEffect(() => {
    if (open && booking) {
      applyItinerary(booking.itinerary && booking.itinerary.status !== "none" ? booking.itinerary : null);
      if (booking.itinerary?.status === "processing") startPoll();
      else fetchItinerary();
    }
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.id]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/bookings/${booking.id}/itinerary`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyItinerary(data.itinerary);
      toast.success("Itinerary uploaded — AI is reading it…");
      startPoll();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const savePointers = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/bookings/${booking.id}/itinerary/pointers`, { key_pointers: pointers });
      applyItinerary(data.itinerary);
      toast.success("Key pointers saved");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const removeItinerary = async () => {
    try {
      await api.delete(`/bookings/${booking.id}/itinerary`);
      applyItinerary(null);
      toast.success("Itinerary removed");
      onChanged?.();
    } catch (e) { toast.error("Could not remove"); }
  };

  const analyze = async () => {
    setUploading(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/itinerary/analyze`);
      applyItinerary(data.itinerary);
      toast.success("Re-reading booking details & special instructions…");
      startPoll();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not analyse");
    } finally { setUploading(false); }
  };

  const downloadOriginal = async () => {
    try {
      const resp = await api.get(`/bookings/${booking.id}/itinerary/file`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open file"); }
  };

  const setP = (i, patch) => setPointers((arr) => arr.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const addP = () => setPointers((arr) => [...arr, { text: "", category: "other", acknowledged: false, source: "manual" }]);
  const delP = (i) => setPointers((arr) => arr.filter((_, idx) => idx !== i));

  const status = it?.status;
  const ackCount = pointers.filter((p) => p.acknowledged).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopPoll(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="booking-itinerary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Itinerary · {booking?.client_name}</DialogTitle>
          <DialogDescription>Upload the guest itinerary — the AI highlights the key pointers your team can't miss.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">
          {/* Upload / file row */}
          <div className="flex items-center justify-between gap-3 border border-zinc-200 rounded-md p-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
              {it?.file_name ? (
                <button onClick={downloadOriginal} className="truncate text-zinc-800 hover:underline flex items-center gap-1" data-testid="itinerary-download">
                  {it.file_name} <Download className="w-3 h-3" />
                </button>
              ) : <span className="text-zinc-400">No itinerary uploaded yet</span>}
            </div>
            <div className="flex gap-2">
              {canUpload && (
                <>
                  <input ref={fileRef} type="file" className="hidden" accept=".pdf,.txt,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif"
                    onChange={(e) => doUpload(e.target.files?.[0])} data-testid="itinerary-file-input" />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="itinerary-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (it ? <RotateCw className="w-4 h-4 mr-1" /> : <Upload className="w-4 h-4 mr-1" />)}
                    {it ? "Replace" : "Upload"}
                  </Button>
                  {it?.file_name && <Button size="sm" variant="ghost" className="text-red-600" onClick={removeItinerary} data-testid="itinerary-remove"><Trash2 className="w-4 h-4" /></Button>}
                </>
              )}
            </div>
          </div>

          {canUpload && (
            <div className="flex items-center justify-between gap-2 text-xs text-zinc-500 flex-wrap -mt-1">
              <span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-500" /> Key pointers are drawn from the itinerary <strong>and</strong> the booking's special instructions.</span>
              <Button size="sm" variant="outline" onClick={analyze} disabled={uploading || status === "processing"} data-testid="itinerary-analyze">
                <Wand2 className="w-3.5 h-3.5 mr-1" /> {pointers.length || it ? "Re-analyze" : "Generate key pointers"}
              </Button>
            </div>
          )}

          {status === "processing" && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-6 justify-center" data-testid="itinerary-processing">
              <Loader2 className="w-4 h-4 animate-spin" /> AI is reading the itinerary…
            </div>
          )}
          {status === "error" && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              <AlertTriangle className="w-4 h-4" /> Couldn't read this file. {it?.error}
            </div>
          )}

          {it?.summary && status === "ready" && (
            <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md p-3"><strong>Summary:</strong> {it.summary}</div>
          )}

          {(status === "ready" || pointers.length > 0) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-overline flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Key pointers — don't miss <span className="text-zinc-400 normal-case tracking-normal">({ackCount}/{pointers.length} done)</span></div>
                <Button size="sm" variant="ghost" onClick={addP} data-testid="itinerary-add-pointer"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {pointers.length === 0 && <div className="text-xs text-zinc-400">No pointers. Upload an itinerary or add one manually.</div>}
                {pointers.map((p, i) => (
                  <div key={p.id || i} className="flex items-start gap-2 border border-zinc-200 rounded-md p-2" data-testid={`itinerary-pointer-${i}`}>
                    <Checkbox checked={!!p.acknowledged} onCheckedChange={(c) => setP(i, { acknowledged: !!c })} className="mt-2" data-testid={`itinerary-ack-${i}`} />
                    <div className="flex-1 min-w-0">
                      <Input value={p.text} onChange={(e) => setP(i, { text: e.target.value })} className={`h-8 text-sm ${p.acknowledged ? "line-through text-zinc-400" : ""}`} placeholder="Pointer…" data-testid={`itinerary-text-${i}`} />
                      <div className="flex items-center gap-2 mt-1">
                        <Select value={p.category || "other"} onValueChange={(v) => setP(i, { category: v })}>
                          <SelectTrigger className="h-7 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-xs capitalize">{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className={`text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 border ${CAT_CLR[p.category] || CAT_CLR.other}`}>{(p.category || "other").replace("_", " ")}</span>
                        {p.source === "ai" && <span className="text-[10px] text-zinc-400">AI</span>}
                      </div>
                    </div>
                    <button onClick={() => delP(i)} className="text-zinc-400 hover:text-red-600 mt-1" data-testid={`itinerary-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {(status === "ready" || pointers.length > 0) && (
            <Button onClick={savePointers} disabled={saving} className="bg-zinc-950" data-testid="itinerary-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save pointers
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
